// Inspired from https://github.com/costidotdev/shadcn-number-input/blob/main/src/components/number-input.tsx
import { useControllableState } from '@radix-ui/react-use-controllable-state';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import * as React from 'react';
import { NumericFormat, type NumericFormatProps } from 'react-number-format';

import { cn } from '../../utils';

import { inputVariants } from './inputVariants';
import { StepperButton } from './StepperButton';

export interface NumberInputProps
  extends Omit<
    NumericFormatProps,
    | 'value'
    | 'onChange'
    | 'onValueChange'
    | 'thousandSeparator'
    | 'decimalSeparator'
    | 'id'
  > {
  id: string;
  stepper?: number;
  placeholder?: string;
  defaultValue?: number;
  min?: number;
  max?: number;
  value?: number | null; // Controlled value
  onChange?: (value: number | null) => void;
  suffix?: string;
  prefix?: string;
  fixedDecimalScale?: boolean;
  decimalScale?: number;
  variant?: 'default' | 'large' | 'small' | 'extraSmall';
  wrapperClassName?: string;
  thousandSeparator?: string;
  decimalSeparator?: string;
  showStepper?: boolean;
}

const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  (
    {
      id,
      stepper,
      placeholder,
      defaultValue,
      min = Number.MIN_SAFE_INTEGER,
      max = Number.MAX_SAFE_INTEGER,
      onChange,
      onBlur,
      onFocus,
      fixedDecimalScale = false,
      decimalScale = 0,
      suffix,
      prefix,
      value: controlledValue,
      variant = 'default',
      className,
      wrapperClassName,
      showStepper = true,
      ...props
    },
    ref,
  ) => {
    const thousandSeparator = props.thousandSeparator || ',';
    const decimalSeparator = props.decimalSeparator || '.';

    const [value, setValue] = useControllableState({
      prop: controlledValue,
      onChange: onChange,
      defaultProp: defaultValue ?? null,
    });

    const handleValueChange = useCallback(
      (values: { floatValue?: number }) => {
        const { floatValue } = values;
        setValue(floatValue ?? null);
      },
      [setValue],
    );

    const handleBlur = (e: React.FocusEvent<HTMLInputElement, Element>) => {
      onBlur?.(e);
      if (value != null) {
        if (value < min) {
          setValue(min);
        } else if (value > max) {
          setValue(max);
        }
      }
    };

    // Internal ref required, as accessing `current` from an external ref that's passed in
    // as a RefCallBack (from react-hook-form for exmaple) is not possible
    const internalRef = useRef<HTMLInputElement | null>(null);

    const handleIncrement = useCallback(() => {
      setValue(
        value == null ? (stepper ?? 1) : Math.min(value + (stepper ?? 1), max),
      );
    }, [stepper, max, value, setValue]);

    const handleDecrement = useCallback(() => {
      setValue(
        value == null ? -(stepper ?? 1) : Math.max(value - (stepper ?? 1), min),
      );
    }, [stepper, min, value, setValue]);

    const handleKeyDown = useCallback(
      (e: KeyboardEvent) => {
        if (document.activeElement === internalRef.current) {
          if (e.key === 'ArrowUp') {
            handleIncrement();
          } else if (e.key === 'ArrowDown') {
            handleDecrement();
          }
        }
      },
      [handleIncrement, handleDecrement],
    );

    useEffect(() => {
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }, [handleKeyDown]);

    // Sync the internal ref with the external ref
    useImperativeHandle(ref, () => internalRef.current as HTMLInputElement);

    return (
      <div
        className={cn(
          'flex items-center focus-within:ring-2 focus-within:ring-ring rounded-sm relative',
          wrapperClassName,
        )}
        onBlur={handleBlur}
        onFocus={onFocus}
      >
        <NumericFormat
          autoComplete="off"
          id={id}
          data-testid={id}
          value={value ?? ''}
          onValueChange={(val, source) => {
            if (source.source === 'prop') return;
            handleValueChange(val);
          }}
          decimalScale={decimalScale}
          fixedDecimalScale={fixedDecimalScale}
          allowNegative={min < 0}
          valueIsNumericString
          max={max}
          min={min}
          suffix={suffix}
          prefix={prefix}
          placeholder={placeholder}
          className={cn(
            inputVariants({ variant }),
            'focus-visible:ring-0',
            className,
          )}
          getInputRef={internalRef}
          {...props}
          thousandSeparator={thousandSeparator}
          decimalSeparator={decimalSeparator}
          isAllowed={({ floatValue }) => {
            // Only block values that are clearly impossible, otherwise user is blocked during typing
            // min / max handled in onBlur function
            if (floatValue && min >= 0 && floatValue < 0) return false;
            return true;
          }}
        />
        {showStepper && (
          <div
            className={cn(
              'absolute flex flex-col h-full top-0 right-0 divide-y divide-input divide-[1px] border-l',
            )}
          >
            <StepperButton
              stepperType="increase"
              onClick={handleIncrement}
              disabled={value === max || !!props.disabled}
            />

            <StepperButton
              stepperType="decrease"
              onClick={handleDecrement}
              disabled={value === min || !!props.disabled}
            />
          </div>
        )}
      </div>
    );
  },
);
NumberInput.displayName = 'NumberInput';

export { NumberInput };
